import { assertNever } from "../../../types/internal/utility";
import {
    type AddNoteAction,
    type SellScreenActionTypes,
    type UpdateProductAction,
} from "../../Actions/SellScreen/SellScreenActions";
import {
    MARK_PRODUCT_AS_ADDING,
    SALE_ADD_ACTION,
    SALE_ADD_CUSTOMER,
    SALE_ADD_GIFTCARD,
    SALE_ADD_NOTE,
    SALE_ADD_ORDER_REFERENCE,
    SALE_ADD_PAYMENT,
    SALE_ADD_PRODUCT,
    SALE_ADD_REFUND_REASON,
    SALE_CHANGE_PRICE_SET,
    SALE_CHANGE_QUANTITY,
    SALE_CLEAR,
    SALE_DISCOUNT_PRODUCT,
    SALE_HIDE_PRODUCT_DETAILS,
    SALE_OVERRIDE_META_DATA,
    SALE_PREVENT_CANCELLATION,
    SALE_REMOVE_CUSTOMER,
    SALE_REMOVE_PRODUCT,
    SALE_RETRIEVE,
    SALE_REVERT_PAYMENT,
    SALE_SET_SURCHARGING,
    SALE_SHOW_PRODUCT_DETAILS,
    SALE_TOGGLE_CASE,
    SALE_UNLOCK_PRODUCT,
    SALE_UPDATE_PRODUCT,
    SALE_UPDATE_PRODUCTS,
    UPDATE_CURRENT_SALE,
} from "../../Actions/SellScreen/SellScreenConstants";
import {
    type AddProductAction,
    type ChangeQuantityAction,
    type DiscountProductAction,
    type RemoveProductAction,
    type ToggleCaseAction,
    type UnlockProductAction,
} from "../../Actions/SellScreen/SellScreenProductActions";
import { type ShopfrontActions } from "../../Utilities/Generic/ActionsHelper";
import { scaleToDecimalPlaces } from "../../Utilities/Generic/Numbers";
import UUID from "../../Utilities/Generic/UUID";
import { getOption, setOption } from "../../Utilities/Platform/Options/Options";
import { type SaleProduct, type SaleState, type SaleSurcharge } from "./SellScreenSaleReducerTypes";

export function freshSale(): SaleState {
    let priceSet = getOption("register_default_price_set");

    if(priceSet === "default" || priceSet === null) {
        priceSet = "";
    }

    return {
        internalId: UUID.generate(),
        products  : [],
        customer  : false,
        payments  : [],
        notes     : {
            internal: "",
            sale    : "",
        },
        productDetails: {
            id   : [],
            index: [],
        },
        totals: {
            cashout  : 0,
            sale     : 0,
            remaining: 0,
            paid     : 0,
            savings  : 0,
            discount : 0,
            change   : 0,
            rounding : 0,
            surcharge: 0,
        },
        registerId    : false,
        userId        : false,
        calculate     : false,
        linkedTo      : false,
        orderReference: "",
        refundReason  : "",
        addingToSale  : null,
        actions       : [],
        surchargeRules: [],
        surcharge     : null,
        metaData      : {},
        isCancellable : true,
        priceSet,
    };
}

function toggleCase(
    products: Array<SaleProduct>,
    index: number
): SaleProduct {
    let quantity = products[index].quantity;

    if(products[index].isCase) {
        quantity = quantity / products[index].caseQuantity;
    } else {
        quantity = quantity * products[index].caseQuantity;
    }

    const prices = products[index].prices;

    if(products[index].edited) {
        // Adjust the price
        const ratio = products[index].prices.price / products[index].quantity;
        const unlockedRatio = products[index].prices.unlockedPrice / products[index].quantity;

        prices.price = ratio * quantity;
        prices.unlockedPrice = unlockedRatio * quantity;
    }

    return {
        ...products[index],
        isCase: !products[index].isCase,
        quantity,
        prices,
    };
}

export function updateRecursiveProduct(
    products: Array<SaleProduct>,
    action:
        ToggleCaseAction |
        UnlockProductAction |
        DiscountProductAction |
        AddNoteAction |
        UpdateProductAction
): Array<SaleProduct> {
    if(!action.indexAddress.length) {
        return products;
    }

    let product: undefined | SaleProduct;
    const index = action.indexAddress[0];

    if(action.indexAddress.length === 1) {
        // Perform the update
        switch(action.type) {
            case SALE_DISCOUNT_PRODUCT:
                product = {
                    ...products[index],
                    edited: true,
                    prices: {
                        ...products[index].prices,
                        price       : action.amount,
                        discountRate: action.amount / products[index].quantity,
                    },
                    canUnlock     : !products[index].special,
                    discountReason: action.reason as string,
                };
                break;
            case SALE_UNLOCK_PRODUCT:
                product = {
                    ...products[index],
                    discountReason: "",
                    edited        : false,
                    prices        : {
                        ...products[index].prices,
                        base        : action.basePrice,
                        discountRate: undefined,
                    },
                };
                break;
            case SALE_TOGGLE_CASE:
                product = toggleCase(products, index);
                break;
            case SALE_ADD_NOTE:
                product = {
                    ...products[index],
                    note: action.note,
                };

                break;
            case SALE_UPDATE_PRODUCT:
                product = {
                    ...products[index],
                    ...action.changes,
                };
                break;
            default:
                console.error("Undefined recursive update type", action);
                product = products[index];
        }
    } else {
        // Get the next layer
        product = {
            ...products[index],
            products: updateRecursiveProduct(products[index].products, {
                ...action,
                indexAddress: action.indexAddress.slice(1),
            }),
        };
    }

    return [
        ...products.slice(0, index),
        product,
        ...products.slice(index + 1),
    ];
}

function removeRecursiveProduct(
    products: Array<SaleProduct>,
    action: RemoveProductAction,
    first = true
): Array<SaleProduct> {
    if(!action.indexAddress.length) {
        return products;
    }

    const index = action.indexAddress[0];

    if(action.indexAddress.length === 1) {
        if(first) {
            return [
                ...products.slice(0, index),
                ...products.slice(index + 1),
            ];
        } else {
            return [
                ...products.slice(0, index),
                {
                    ...products[index],
                    deleted: true,
                },
                ...products.slice(index + 1),
            ];
        }
    } else {
        return [
            ...products.slice(0, index),
            {
                ...products[index],
                products: removeRecursiveProduct(products[index].products, {
                    ...action,
                    indexAddress: action.indexAddress.slice(1),
                }, false),
            },
            ...products.slice(index + 1),
        ];
    }
}

function addRecursiveProduct(
    products: Array<SaleProduct>,
    action: AddProductAction
): Array<SaleProduct> {
    const indexAddress = action.indexAddress;

    if(indexAddress.length === 0) {
        return [
            ...products,
            action.product,
        ];
    } else {
        const index = indexAddress[0];

        return [
            ...products.slice(0, index),
            {
                ...products[index],
                products: addRecursiveProduct(products[index].products, {
                    ...action,
                    indexAddress: indexAddress.slice(1),
                }),
            },
            ...products.slice(index + 1),
        ];
    }
}

export function updateProductQuantity(
    products: Array<SaleProduct>,
    action: ChangeQuantityAction
): Array<SaleProduct> {
    if(products[action.index].lockQuantity) {
        return products;
    }

    let quantity = action.amount;

    if(!action.absolute) {
        quantity += products[action.index].quantity;
    }

    const product = {
        ...products[action.index],
        quantity,
    };

    if(product.edited) {
        // Adjust price
        // NOTE: This should probably be moved
        const oldQuantity = products[action.index].quantity;
        const ratio = products[action.index].prices.price / oldQuantity;
        const unlockedRatio = products[action.index].prices.unlockedPrice / oldQuantity;
        const additionalBaseRatio = products[action.index].prices.additionalPrice[0] / oldQuantity;
        const additionalPriceRatio = products[action.index].prices.additionalPrice[1] / oldQuantity;

        product.prices.price = ratio * quantity;
        product.prices.unlockedPrice = unlockedRatio * quantity;
        product.prices.additionalPrice = [ additionalBaseRatio * quantity, additionalPriceRatio * quantity ];
    }

    return [
        ...products.slice(0, action.index),
        product,
        ...products.slice(action.index + 1),
    ];
}

interface SaleSurchargeGroup {
    fixed: null | SaleSurcharge;
    percentage: null | SaleSurcharge;
}

/**
 * Calculates the sale totals
 * @param state
 * @returns {SaleState}
 */
export function calculateTotals(state: SaleState): SaleState {
    const totals = {
        sale     : 0,
        remaining: 0,
        paid     : 0,
        savings  : 0,
        discount : 0,
        change   : 0,
        cashout  : 0,
        rounding : 0,
        surcharge: 0,
    };

    // Calculate the sale total
    let products = state.products;
    let discount = 0;
    let savings = 0;
    let hasNegative = false;

    for(let i = 0, l = products.length; i < l; i++) {
        let price = products[i].prices.price;

        if(!products[i].quantity) {
            price = 0;
        }

        totals.sale += price;

        if(products[i].type === "Voucher") {
            continue;
        }

        if(products[i].quantity < 0) {
            hasNegative = true;
        }

        const normal = scaleToDecimalPlaces(products[i].prices.normal);
        const unlocked = scaleToDecimalPlaces(products[i].prices.unlockedPrice);
        const actual = scaleToDecimalPlaces(products[i].prices.price);
        const componentTotal = scaleToDecimalPlaces(products[i].prices.additionalPrice[1]);
        const componentUnlocked = scaleToDecimalPlaces(products[i].prices.additionalPrice[0]);

        if(isNaN(actual)) {
            // Loaded from a parked sale
            continue;
        }

        // Note: Not sure how to handle price lists and promotions being handled at the same time
        if(!products[i].priceList) {
            if(normal !== actual || unlocked !== actual) {
                if(products[i].edited && products[i].canUnlock) {
                    discount += unlocked - actual;
                } else if(!products[i].edited) {
                    savings += normal - actual + componentTotal;
                    discount += componentUnlocked - componentTotal;
                }
            }
        }
    }

    // Calculate the remaining
    const payments = state.payments;

    for(let i = 0, l = payments.length; i < l; i++) {
        if(payments[i].status === "completed") {
            totals.paid += payments[i].amount;

            if(typeof payments[i].rounding === "number") {
                totals.paid -= payments[i].rounding as number;
            }

            totals.cashout = payments[i].cashout;
        }
    }

    // Calculate the surcharge
    if(state.surchargeRules.length && !hasNegative) {
        // Get the highest sale wide surcharges
        let saleFixed: null | SaleSurcharge = null;
        let salePercent: null | SaleSurcharge = null;

        const categorySurcharges: Record<string, SaleSurchargeGroup> = {};

        let hasTagSurcharges = false;
        const tagSurcharges: Record<string, SaleSurchargeGroup> = {};

        // Prepare the possible surcharges for the sale
        for(const rule of state.surchargeRules) {
            // Prepare the sale only surcharges
            if(rule.appliesTo === null) {
                if(rule.type === "PERCENTAGE") {
                    // Get the highest percentage surcharge
                    if(salePercent === null || salePercent.amount < rule.amount) {
                        salePercent = rule;
                    }
                } else if(rule.type === "FIXED") {
                    // Get the highest fixed amount surcharge
                    if(saleFixed === null || saleFixed.amount < rule.amount) {
                        saleFixed = rule;
                    }
                } else {
                    return assertNever(rule.type);
                }

                continue;
            }

            // Prepare the surcharges that are limited to classifications
            for(const item of rule.appliesTo) {
                let surcharges: SaleSurchargeGroup;

                if(item.type === "Tag") {
                    hasTagSurcharges = true;

                    if(!(item.id in tagSurcharges)) {
                        tagSurcharges[item.id] = {
                            fixed     : null,
                            percentage: null,
                        };
                    }

                    surcharges = tagSurcharges[item.id];
                } else if(item.type === "Category") {
                    if(!(item.id in categorySurcharges)) {
                        categorySurcharges[item.id] = {
                            fixed     : null,
                            percentage: null,
                        };
                    }

                    surcharges = categorySurcharges[item.id];
                } else {
                    return assertNever(item.type);
                }

                if(rule.type === "PERCENTAGE") {
                    if(surcharges.percentage === null) {
                        surcharges.percentage = rule;
                    } else if(surcharges.percentage.amount < rule.amount) {
                        surcharges.percentage = rule;
                    }
                } else if(rule.type === "FIXED") {
                    if(surcharges.fixed === null) {
                        surcharges.fixed = rule;
                    } else if(surcharges.fixed.amount < rule.amount) {
                        surcharges.fixed = rule;
                    }
                } else {
                    return assertNever(rule.type);
                }
            }
        }

        let surchargeAmount = 0;
        let surchargeApplicable = 0;

        const newProducts: Array<SaleProduct> = [];

        // Calculate surcharges for products
        for(let i = 0, l = state.products.length; i < l; i++) {
            const product = state.products[i];

            let active: null | { id: number; amount: number; } = null;

            const price = product.quantity === 0 ? 0 : product.prices.price;

            // Check category surcharges
            if(product.categoryId && product.categoryId in categorySurcharges) {
                const { fixed, percentage } = categorySurcharges[product.categoryId];

                if(fixed) {
                    active = {
                        id    : fixed.id,
                        amount: fixed.amount,
                    };
                }

                if(percentage) {
                    const amount = price * percentage.amount / 100;

                    if(!active || active.amount < amount) {
                        active = {
                            id: percentage.id,
                            amount,
                        };
                    }
                }
            }

            // Check tag surcharges
            if(hasTagSurcharges) {
                for(const tag of product.tags) {
                    if(!(tag in tagSurcharges)) {
                        continue;
                    }

                    const { fixed, percentage } = tagSurcharges[tag];

                    if(fixed) {
                        if(!active || active.amount < fixed.amount) {
                            active = {
                                id    : fixed.id,
                                amount: fixed.amount,
                            };
                        }
                    }

                    if(percentage) {
                        const amount = price * percentage.amount / 100;

                        if(!active || active.amount < amount) {
                            active = {
                                id: percentage.id,
                                amount,
                            };
                        }
                    }
                }
            }

            // Check to see if the sale's percentage surcharge is greater than the product specific surcharge
            if(active && salePercent) {
                const amount = price * salePercent.amount / 100;

                if(active.amount < amount) {
                    active = null;
                }
            }

            if(active) {
                newProducts.push({
                    ...product,
                    surcharge: active,
                });

                surchargeAmount += active.amount;
            } else {
                newProducts.push({
                    ...product,
                    surcharge: null,
                });

                surchargeApplicable += price;
            }
        }

        products = newProducts;

        // Calculate sale wide surcharge
        let saleSurchargeId: null | number = null;
        let saleSurcharge = 0;

        if(salePercent) {
            saleSurchargeId = salePercent.id;
            saleSurcharge = surchargeApplicable * salePercent.amount / 100;
        }

        if(saleFixed && saleSurcharge < saleFixed.amount) {
            saleSurchargeId = saleFixed.id;
            saleSurcharge = saleFixed.amount;
        }

        totals.surcharge = surchargeAmount + saleSurcharge;
        totals.sale += surchargeAmount + saleSurcharge;

        if(saleSurchargeId === null) {
            state.surcharge = null;
        } else {
            // This is bad as well
            state.surcharge = {
                id    : saleSurchargeId,
                amount: saleSurcharge,
            };
        }
    }

    totals.remaining = scaleToDecimalPlaces(totals.sale - totals.paid);
    totals.paid = scaleToDecimalPlaces(totals.paid);
    totals.sale = scaleToDecimalPlaces(totals.sale);
    totals.discount = scaleToDecimalPlaces(discount);
    totals.savings = scaleToDecimalPlaces(savings);
    totals.surcharge = scaleToDecimalPlaces(totals.surcharge);

    return {
        ...state,
        products,
        totals: totals,
    };
}

function SellScreenSaleReducer(
    state: SaleState | undefined,
    action: ShopfrontActions<SellScreenActionTypes>
): SaleState {
    if(typeof state === "undefined") {
        let startingSale: SaleState | null = getOption("current_sale");

        if(startingSale !== null) {
            console.log("Continuing sale", startingSale);
        } else {
            startingSale = freshSale();
        }

        if(!startingSale.surchargeRules) {
            startingSale.surchargeRules = [];
        }

        return {
            ...startingSale,
            productDetails: { // For some reason product details is sometimes corrupted
                id   : [], // I can't correctly replicate it all the time so this is the best
                index: [], // solution for the moment
            },
        };
    }

    let sale = state;
    let actions;

    // Whether to update the local sale state or not
    let updateOptions = true;

    // Determine the update to make to the sale
    switch(action.type) {
        case SALE_ADD_PRODUCT:
            sale = {
                ...state,
                products : addRecursiveProduct(state.products, action),
                calculate: true,
            };
            break;
        case SALE_REMOVE_PRODUCT:
            sale = {
                ...state,
                products : removeRecursiveProduct(state.products, action),
                calculate: true,
            };
            break;
        case SALE_CHANGE_QUANTITY:
            sale = {
                ...state,
                products : updateProductQuantity(state.products, action),
                calculate: true,
            };
            break;
        case SALE_TOGGLE_CASE:
            sale = {
                ...state,
                products : updateRecursiveProduct(state.products, action),
                calculate: true,
            };

            break;
        case SALE_UPDATE_PRODUCTS:
            // This is applied after prices are calculated
            sale = calculateTotals({
                ...state,
                products : action.products,
                calculate: false,
            });
            break;
        case SALE_DISCOUNT_PRODUCT:
            sale = {
                ...state,
                products : updateRecursiveProduct(state.products, action),
                calculate: true,
            };
            break;
        case SALE_UNLOCK_PRODUCT:
            sale = {
                ...state,
                products : updateRecursiveProduct(state.products, action),
                calculate: true,
            };
            break;
        case SALE_ADD_CUSTOMER:
            sale = {
                ...state,
                customer: {
                    ...action.customer,
                },
                calculate: true,
            };
            break;
        case SALE_REMOVE_CUSTOMER:
            sale = {
                ...state,
                customer : false,
                calculate: true,
            };
            break;
        case SALE_ADD_NOTE:
            if(Array.isArray(action.to)) {
                sale = {
                    ...state,
                    products: updateRecursiveProduct(state.products, {
                        ...action,
                        indexAddress: action.to,
                    }),
                };
            } else {
                sale = {
                    ...state,
                    notes: {
                        ...state.notes,
                        [action.to]: action.note,
                    },
                };
            }

            break;
        case SALE_ADD_ORDER_REFERENCE:
            sale = {
                ...sale,
                orderReference: action.reference,
            };
            break;
        case SALE_ADD_REFUND_REASON:
            sale = {
                ...sale,
                refundReason: action.reason,
            };
            break;
        case SALE_ADD_PAYMENT:
            sale = calculateTotals({
                ...state,
                payments: [
                    ...state.payments,
                    action.payment,
                ],
            });
            break;
        case SALE_REVERT_PAYMENT:
            sale = {
                ...state,
                payments: [
                    ...state.payments.slice(0, action.index),
                    {
                        ...state.payments[action.index],
                        reverted: true,
                    },
                    ...state.payments.slice(action.index + 1),
                ],
            };
            break;
        case SALE_RETRIEVE:
            sale = calculateTotals({
                ...action.state,
                calculate: true,
            });
            break;
        case SALE_CLEAR:
            sale = freshSale();
            updateOptions = !action.ignoreBroadcast;
            break;
        case SALE_SHOW_PRODUCT_DETAILS:
            updateOptions = false; // Purely UI
            sale = {
                ...state,
                productDetails: {
                    id: [
                        ...state.productDetails.id,
                        action.id,
                    ],
                    index: [
                        ...state.productDetails.index,
                        action.index as number,
                    ],
                },
            };
            break;
        case SALE_HIDE_PRODUCT_DETAILS:
            updateOptions = false; // Purely UI
            sale = {
                ...state,
                productDetails: {
                    id   : [],
                    index: [],
                },
            };
            break;
        case SALE_ADD_GIFTCARD:
            sale = calculateTotals({
                ...state,
                products: [
                    ...state.products,
                    action.product,
                ],
            });
            break;
        case SALE_CHANGE_PRICE_SET:
            sale = {
                ...state,
                priceSet : action.priceSet === sale.priceSet ? "" : action.priceSet,
                calculate: true,
            };
            break;
        case MARK_PRODUCT_AS_ADDING:
            sale = {
                ...state,
                addingToSale: action.id,
            };
            break;
        case SALE_UPDATE_PRODUCT:
            sale = {
                ...state,
                products : updateRecursiveProduct(state.products, action),
                calculate: true,
            };
            break;
        case SALE_ADD_ACTION:
            if(
                [ "release", "beta" ].includes(__VERSION__.channel) &&
                (action.affecting as string | false) === false
            ) {
                break;
            }

            actions = [];

            if(sale.actions) {
                actions.push(...sale.actions);
            }

            actions.push({
                timestamp: action.timestamp,
                action   : action.action,
                value    : action.value,
                affecting: action.affecting,
                user     : action.user,
            });

            sale = {
                ...state,
                actions: actions,
            };
            break;
        case SALE_SET_SURCHARGING:
            sale = calculateTotals({
                ...state,
                surchargeRules: action.rules,
            });
            break;
        case UPDATE_CURRENT_SALE:
            sale = action.sale;
            updateOptions = false;
            break;
        case SALE_OVERRIDE_META_DATA:
            sale = {
                ...state,
                metaData: action.metaData,
            };
            break;
        case SALE_PREVENT_CANCELLATION:
            sale = {
                ...state,
                isCancellable: action.isCancellable,
            };
            break;
        default:
            // Other actions that aren't from the sales will be sent
            // through to here, so we need to make sure we don't send
            // any updates that don't touch the sale state.
            updateOptions = false;
    }

    // Check if we should update the current sale,
    // if it comes from a broadcast or if the sale
    // requires calculation then it should not be
    // updated. If it requires calculation, the
    // calculation result will be updated later
    // anyway so there is no need (and could
    // potentially result in multiple calculations).
    if(updateOptions && !sale.calculate) {
        setOption("current_sale", sale, false);
    }

    return sale;
}

export default SellScreenSaleReducer;
